import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface FormState {
  status: 'idle' | 'loading' | 'success' | 'error';
}

function ContactForm() {
  const [formState, setFormState] = useState<FormState>({ status: 'idle' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const msg = (e.currentTarget.querySelector('textarea[name="message"]') as HTMLTextAreaElement)?.value ?? '';
    if (msg.length > 500) return;
    setFormState({ status: 'loading' });
    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, value.toString()));
    try {
      const res = await fetch('https://readdy.ai/api/form/d6uqtim7ipaugi04ldhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) { setFormState({ status: 'success' }); form.reset(); }
      else setFormState({ status: 'error' });
    } catch { setFormState({ status: 'error' }); }
  };

  if (formState.status === 'success') {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-check-line text-green-600 text-2xl" />
        </div>
        <h3 className="font-bold text-[#03143A] text-xl mb-2">Message Sent Successfully!</h3>
        <p className="text-gray-500">We&apos;ll contact you within 24 hours to discuss your facility needs.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-bold text-[#03143A] mb-2 uppercase tracking-wide">Full Name *</label>
          <input type="text" name="name" required placeholder="John Smith" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-bold text-[#03143A] mb-2 uppercase tracking-wide">Company Name</label>
          <input type="text" name="company" placeholder="Your Company Inc." className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors" />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-bold text-[#03143A] mb-2 uppercase tracking-wide">Email Address *</label>
          <input type="email" name="email" required placeholder="you@company.com" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-bold text-[#03143A] mb-2 uppercase tracking-wide">Phone Number *</label>
          <input type="tel" name="phone" required placeholder="(713) 000-0000" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold text-[#03143A] mb-2 uppercase tracking-wide">Service Needed</label>
        <select name="service" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors bg-white cursor-pointer">
          <option value="">Select a service...</option>
          <option value="Commercial Cleaning">Commercial Cleaning</option>
          <option value="Janitorial Services">Janitorial Services</option>
          <option value="Office Cleaning">Office Cleaning</option>
          <option value="Floor Care">Floor Care &amp; Maintenance</option>
          <option value="Post-Construction Cleaning">Post-Construction Cleaning</option>
          <option value="Specialized Cleaning">Specialized Facility Cleaning</option>
          <option value="Professional Staffing">Professional Staffing</option>
          <option value="Valet Parking">Valet Parking</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-bold text-[#03143A] mb-2 uppercase tracking-wide">Message</label>
        <textarea name="message" rows={5} placeholder="Tell us about your facility, location, and cleaning needs..." maxLength={500} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors resize-none" />
        <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
      </div>
      {formState.status === 'error' && (
        <p className="text-red-500 text-sm">Something went wrong. Please try again or call us at (713) 338-2553.</p>
      )}
      <button
        type="submit"
        disabled={formState.status === 'loading'}
        className="w-full py-4 bg-[#C70532] hover:bg-[#a80428] disabled:bg-gray-300 text-white font-bold rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap text-lg"
      >
        {formState.status === 'loading' ? 'Sending...' : 'Send Message & Request Quote'}
      </button>
    </form>
  );
}

export default function ContactPage() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 bg-[#03143A] overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://primefacilityservicesgroup.com/images/contact-us-background.png" alt="Contact Prime Facility Services Group" className="w-full h-full object-cover object-top opacity-20" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
            <span className="inline-block px-4 py-1.5 bg-[#C70532]/20 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-6">Get In Touch</span>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-4 leading-tight">Let&apos;s Transform Your Facility Together</h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">Ready to elevate your facility? Contact our Houston team for a free, no-obligation assessment and custom cleaning plan.</p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-[#FAF8F5]">
          <div className="max-w-7xl mx-auto px-6">
            <div ref={ref} className={`grid lg:grid-cols-2 gap-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* Left: Info */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold text-[#03143A] mb-2">Get in Touch Instantly</h2>
                  <p className="text-gray-500">Our team is available 24/7 for emergency needs and responds to all inquiries within 24 hours.</p>
                </div>

                <div className="space-y-4">
                  <a href="tel:+17133382553" className="flex items-center gap-5 p-5 bg-[#03143A] rounded-2xl hover:bg-[#03143A]/90 transition-colors cursor-pointer group">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#C70532] rounded-xl flex-shrink-0">
                      <i className="ri-phone-fill text-white text-xl" />
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wide">24/7 Emergency Line</p>
                      <p className="text-white font-bold text-xl">(713) 338-2553</p>
                    </div>
                  </a>

                  <a href="mailto:its@primefacilityservicesgroup.com" className="flex items-center gap-5 p-5 bg-white rounded-2xl border border-gray-100 hover:border-[#C70532]/30 transition-colors cursor-pointer group">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#C70532]/10 rounded-xl flex-shrink-0 group-hover:bg-[#C70532] transition-colors">
                      <i className="ri-mail-line text-[#C70532] group-hover:text-white text-xl transition-colors" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs uppercase tracking-wide">Email Us</p>
                      <p className="text-[#03143A] font-semibold text-sm break-all">its@primefacilityservicesgroup.com</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-5 p-5 bg-white rounded-2xl border border-gray-100">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#03143A]/10 rounded-xl flex-shrink-0">
                      <i className="ri-map-pin-2-line text-[#03143A] text-xl" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs uppercase tracking-wide">Headquarters</p>
                      <p className="text-[#03143A] font-semibold">8303 Westglen Dr, Houston, TX 77063</p>
                      <p className="text-[#C70532] text-sm font-medium">Serving Greater Houston Area</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h3 className="font-bold text-[#03143A] mb-4">Service Hours</h3>
                  <div className="space-y-3">
                    {[
                      { day: 'Monday – Friday', hours: '24/7 Available' },
                      { day: 'Saturday – Sunday', hours: '24/7 Available' },
                    ].map((item) => (
                      <div key={item.day} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                        <span className="text-gray-500 text-sm">{item.day}</span>
                        <span className="font-bold text-[#03143A] text-sm">{item.hours}</span>
                      </div>
                    ))}
                    <div className="mt-3 px-4 py-3 bg-[#C70532]/10 rounded-xl">
                      <p className="text-[#C70532] font-semibold text-sm flex items-center gap-2">
                        <i className="ri-alarm-warning-line" />
                        Emergency Services Always Available
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-white rounded-3xl p-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-[#03143A] mb-1">Request Your Free Quote</h2>
                <p className="text-gray-500 text-sm mb-6">We&apos;ll respond within 24 hours with a customized facility services proposal.</p>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="bg-white">
          <iframe
            title="Prime Facility Services Group Houston TX"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d221789.7394742903!2d-95.66908!3d29.8162!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640b8b4488d8501%3A0xca12b4ca38d67b6d!2sHouston%2C%20TX!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </section>
      </main>
      <Footer />
    </>
  );
}
